"use strict";
// How long the mairie has to instruct the dossier.
//
// The delay is a statutory consequence of what was deposited, not a judgement
// call, so it is derived rather than asked for. That matters because the
// commercial is the one who lodges a `deposit_mode: CLIENT` dossier, and they
// have no business choosing an instruction period — but until this existed the
// only way to record a deposit was the architect's form, which demanded one.
//
// **The architect stays the authority.** This produces the opening value; they
// may correct it afterwards (`project/file/instruction-delay`), and a correction
// notifies the commercial. Derivation is a default, never a lock.
//
// The bases, per the Code de l'urbanisme, for the only thing this product ever
// files — work on an existing individual house (véranda, pergola, piscine):
//
//   • déclaration préalable ...................... 1 month  (R.423-23 a)
//   • permis de construire, maison individuelle .. 2 months (R.423-23 b)
//   • permis modificatif .......................... 2 months (follows the PC)
//
// and one month is added inside an ABF perimeter — abords de monument
// historique or site patrimonial remarquable (R.423-24), which is exactly what
// the `_ABF` rider on `planning_file_type` records.
Object.defineProperty(exports, "__esModule", { value: true });
exports.INSTRUCTION_DELAY_BY_TYPE = exports.formatInstructionDelay = exports.instructionDelayInterval = exports.instructionDelayMonths = exports.isAbf = exports.planningFileBase = exports.ABF_EXTRA_MONTHS = exports.INSTRUCTION_MONTHS_BY_BASE = exports.PlanningFileBase = void 0;
const file_type_1 = require("../planning/file-type");
/** The three bases a `planning_file_type` can be built on. */
exports.PlanningFileBase = {
    DP: 'DP',
    PC: 'PC',
    PCM: 'PCM',
};
/** Statutory instruction period per base, in whole months. */
exports.INSTRUCTION_MONTHS_BY_BASE = {
    [exports.PlanningFileBase.DP]: 1,
    [exports.PlanningFileBase.PC]: 2,
    [exports.PlanningFileBase.PCM]: 2,
};
/** Added inside an ABF perimeter (R.423-24). */
exports.ABF_EXTRA_MONTHS = 1;
/**
 * The base of a composed `planning_file_type`.
 *
 * Order matters: `PCM*` must be tested before `PC*`, or every modificatif reads
 * as a plain permis de construire — they share a prefix and the catalogue has
 * both.
 */
const planningFileBase = (planningFileType) => {
    if (!planningFileType)
        return null;
    if (planningFileType.startsWith('PCM'))
        return exports.PlanningFileBase.PCM;
    if (planningFileType.startsWith('PC'))
        return exports.PlanningFileBase.PC;
    if (planningFileType.startsWith('DP'))
        return exports.PlanningFileBase.DP;
    return null;
};
exports.planningFileBase = planningFileBase;
/** Whether the type carries the ABF rider. */
const isAbf = (planningFileType) => Boolean(planningFileType?.endsWith('_ABF'));
exports.isAbf = isAbf;
/**
 * The instruction period for a deposited dossier, in whole months.
 *
 * `null` when the type is absent or unrecognised — the caller must then leave
 * the delay unset rather than guess, so the architect is asked for it instead
 * of the dossier carrying a fabricated deadline.
 */
const instructionDelayMonths = (planningFileType) => {
    const base = (0, exports.planningFileBase)(planningFileType);
    if (!base)
        return null;
    return (exports.INSTRUCTION_MONTHS_BY_BASE[base] +
        ((0, exports.isAbf)(planningFileType) ? exports.ABF_EXTRA_MONTHS : 0));
};
exports.instructionDelayMonths = instructionDelayMonths;
/**
 * The same value as a Postgres interval literal.
 *
 * `<n> mons` is the exact spelling the architect's delay picker already writes
 * (`synthesis/8.tsx`), so a derived delay and a hand-picked one are
 * indistinguishable in the column — which is what lets the architect's override
 * be a plain overwrite.
 */
const instructionDelayInterval = (planningFileType) => {
    const months = (0, exports.instructionDelayMonths)(planningFileType);
    return months === null ? null : `${months} mons`;
};
exports.instructionDelayInterval = instructionDelayInterval;
/** Human-readable, for the emails and the hub. */
const formatInstructionDelay = (months) => months === 1 ? '1 mois' : `${months} mois`;
exports.formatInstructionDelay = formatInstructionDelay;
/** Every catalogue member, with the delay it derives — the table, materialised. */
exports.INSTRUCTION_DELAY_BY_TYPE = Object.values(file_type_1.PlanningFileType).reduce((acc, type) => {
    const months = (0, exports.instructionDelayMonths)(type);
    if (months !== null)
        acc[type] = months;
    return acc;
}, {});
//# sourceMappingURL=instruction-delay.js.map